import React, { useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable
} from '@tanstack/react-table'

import { OrderListItems } from './InvoiceList'


const columnHelper = createColumnHelper<OrderListItems>()

export default function OrderItemList({ data }: { data: OrderListItems[] }) {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 })

  const columns = [
    columnHelper.accessor('name', {
      header: 'Item Name',
      cell: (info) => (
        <Typography variant='body2' fontWeight='medium'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('count', {
      header: 'Item Count',
      cell: (info) => (
        <Typography variant='body2' fontFamily='monospace'>
          {info.getValue()}
        </Typography>
      )
    }),
  ]

  const table = useReactTable({
    data,
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  })

  const { pageIndex, pageSize } = table.getState().pagination

  // Keep page in range if data changes
  const totalRows = table.getPrePaginationRowModel().rows.length
  useEffect(() => {
    const maxPage = Math.max(Math.ceil(totalRows / pageSize) - 1, 0)
    if (pageIndex > maxPage) table.setPageIndex(maxPage)
  }, [pageIndex, pageSize, totalRows, table])

  const paginatedRowCount = table.getFilteredRowModel
    ? table.getFilteredRowModel().rows.length
    : totalRows

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: 'action.hover' }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ py: 2.5 }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty state */}
        {table?.getRowModel().rows.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color='text.secondary'>No Statement found</Typography>
          </Box>
        )}

        {/* Pagination controls */}
        <TablePagination
          component='div'
          count={paginatedRowCount}
          page={pageIndex}
          onPageChange={(_evt, newPage) => table.setPageIndex(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(evt) => {
            const newSize = parseInt(evt.target.value, 10)
            table.setPageSize(newSize)
            table.setPageIndex(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          showFirstButton
          showLastButton
        />
      </TableContainer>
    </Box>
  )
}
